/**
 * Main application entry point - React 19+ & Firebase v11+ optimized
 * Provides global error handling, performance monitoring, and Firebase initialization
 * @module main
 */

import { Component, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.jsx'
import { analytics, performance } from './config/firebase.js'

/**
 * Global Error Boundary - React 19+ class component for error handling
 * Integrates with Firebase Analytics for error reporting
 */
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application error:', error, errorInfo)
    
    // Report to Firebase Analytics (production only)
    if (analytics) {
      import('firebase/analytics').then(({ logEvent }) => {
        logEvent(analytics, 'exception', {
          description: error.message,
          fatal: false,
          custom_map: {
            component_stack: errorInfo.componentStack,
            error_boundary: true,
          },
        }).catch(analyticsError => {
          console.warn('Analytics error reporting failed:', analyticsError)
        })
      }).catch(importError => {
        console.warn('Analytics import failed:', importError)
      })
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
          <div className="card p-4 text-center" style={{ maxWidth: '400px' }}>
            <h2 className="text-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              Something went wrong
            </h2>
            <p className="text-muted mb-3">
              We&apos;re sorry, but the application encountered an unexpected error.
            </p>
            <button 
              className="btn btn-primary" 
              onClick={this.handleReset}
              type="button"
            >
              <i className="bi bi-arrow-clockwise me-2" />
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Initialize performance monitoring trace for app startup
if (performance) {
  import('firebase/performance').then(({ trace }) => {
    const appStartTrace = trace(performance, 'app_startup')
    appStartTrace.start()
    
    // Complete trace after initial render
    setTimeout(() => {
      appStartTrace.stop().catch(error => {
        console.warn('Performance trace completion failed:', error)
      })
    }, 100)
  }).catch(perfError => {
    console.warn('Performance monitoring initialization failed:', perfError)
  })
}

// React 19+ createRoot with optimized error boundaries
const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>,
)
